import { exerciseTypes } from 'z-js-commons';
import { CORRECT, MISSING } from './exercise';
import { VERB_URL, X_API_ZANICHELLI } from '../constants/constants';
import { parseHomeworkId } from './homework';

const getActor = () => {
    return {
        objectType: 'Agent',
        account: {
            homePage: '',
            name: '',
        },
    };
};

const getContext = () => {
    return {
        platform: 'Viewer',
    };
};

const getStartedContext = (parent) => {
    if (parent) {
        return {
            platform: 'Viewer',
            contextActivities: {
                parent: [
                    {
                        id: `${X_API_ZANICHELLI}/keys/opened_from/${parent}`,
                    },
                ],
            },
        };
    }

    return getContext();
};

export const getOnSelectExtensions = (bookTitle, courseTitle, subject, exerciseStatus, difficulty, index, isbn) => {
    let extensions = {};
    if (isbn) {
        extensions[`${X_API_ZANICHELLI}/keys/book/isbn`] = isbn;
    }
    if (bookTitle) {
        extensions[`${X_API_ZANICHELLI}/keys/book/bookTitle`] = bookTitle;
    }
    if (courseTitle) {
        extensions[`${X_API_ZANICHELLI}/keys/book/courseTitle`] = courseTitle;
    }
    if (subject) {
        extensions[`${X_API_ZANICHELLI}/keys/exercise/matter`] = subject;
    }
    extensions[`${X_API_ZANICHELLI}/keys/exercise/type`] = exerciseStatus === 'MISSING' ? 'empty' : 'completed';
    extensions[`${X_API_ZANICHELLI}/keys/exercise/difficulty`] = difficulty;
    extensions[`${X_API_ZANICHELLI}/keys/exercise/number`] = index;

    return extensions;
};

export const getOnCorrectionExtensions = (bookTitle, courseTitle, subject, difficulty, exerciseNumber, isbn) => {
    let extensions = {};
    if (isbn) {
        extensions[`${X_API_ZANICHELLI}/keys/book/isbn`] = isbn;
    }
    if (bookTitle) {
        extensions[`${X_API_ZANICHELLI}/keys/book/bookTitle`] = bookTitle;
    }
    if (courseTitle) {
        extensions[`${X_API_ZANICHELLI}/keys/book/courseTitle`] = courseTitle;
    }
    if (subject) {
        extensions[`${X_API_ZANICHELLI}/keys/exercise/matter`] = subject;
    }
    extensions[`${X_API_ZANICHELLI}/keys/activities/type`] = 'exercise';
    extensions[`${X_API_ZANICHELLI}/keys/exercise/difficulty`] = difficulty;
    extensions[`${X_API_ZANICHELLI}/keys/exercise/number`] = exerciseNumber;

    return extensions;
};

export const getOnTerminateExtensions = (bookTitle, subject, exercisesCount, exercises, courseTitle, profile, isbn) => {
    let extensions = {};
    if (isbn) {
        extensions[`${X_API_ZANICHELLI}/keys/book/isbn`] = isbn;
    }
    if (bookTitle) {
        extensions[`${X_API_ZANICHELLI}/keys/book/bookTitle`] = bookTitle;
    }
    if (subject) {
        extensions[`${X_API_ZANICHELLI}/keys/exercise/matter`] = subject;
    }
    if (courseTitle) {
        extensions[`${X_API_ZANICHELLI}/keys/homework/courseTitle`] = courseTitle;
    }
    extensions[`${X_API_ZANICHELLI}/keys/activities/type`] = 'homework';
    extensions[`${X_API_ZANICHELLI}/keys/homework/exercisesCount`] = exercisesCount;
    extensions[`${X_API_ZANICHELLI}/keys/homework/exercises`] = exercises;
    extensions[`${X_API_ZANICHELLI}/keys/homework/profile`] = profile;

    return extensions;
};

export const onSelectExercise = (state, action) => {
    const { index = 0 } = action.payload || {};
    const exercise = state.homework.exercises[index];
    const type = exerciseTypes.getExerciseTypeFromName(exercise.typeLabel).split(' ').join('-').toLowerCase();
    const subject = state.homework.subjects && state.homework.subjects.length > 0 && state.homework.subjects[0].name;
    const bookTitle = state.homework.bookTitle;
    const correctionStatus = state.status.correctionsStatus.find((correctionStatus) => correctionStatus.exerciseId === exercise.id);
    const exerciseStatus = correctionStatus ? correctionStatus.state : MISSING;
    const isbn = state?.homework?.isbn;
    const courseTitle = state?.homework?.courseTitle;

    return {
        actor: getActor(),
        verb: {
            id: `${VERB_URL}/verbs/selected`,
        },
        object: {
            objectType: 'Activity',
            id: `${X_API_ZANICHELLI}/activities/exercise/${exercise.id}`,
            definition: {
                type: `${X_API_ZANICHELLI}/xapi/activities/exercise/${type}`,
                extensions: getOnSelectExtensions(bookTitle, courseTitle, subject, exerciseStatus, exercise.difficulty, index, isbn),
            },
        },
        context: getContext(),
    };
};

export const onShowExerciseCorrection = (state, action) => {
    const { exerciseId, state: correctionState } = action.payload;
    const exercise = state.homework.exercises.find((exercise) => exercise.id === exerciseId);
    const exerciseNumber = state.homework.exercises.findIndex((exercise) => exercise.id === exerciseId);
    const type = exerciseTypes.getExerciseTypeFromName(exercise.typeLabel).split(' ').join('-').toLowerCase();
    const subject = state.homework.subjects && state.homework.subjects.length > 0 && state.homework.subjects[0].name;
    const bookTitle = state.homework.bookTitle;
    const isbn = state?.homework?.isbn;
    const courseTitle = state?.homework?.courseTitle;

    return {
        actor: getActor(),
        verb: {
            id: `${VERB_URL}/verbs/responded`,
        },
        object: {
            objectType: 'Activity',
            id: `${X_API_ZANICHELLI}/activities/exercise/${exercise.id}`,
            definition: {
                type: `${X_API_ZANICHELLI}/xapi/activities/exercise/${type}`,
                extensions: getOnCorrectionExtensions(bookTitle, courseTitle, subject, exercise.difficulty, exerciseNumber, isbn),
            },
        },
        result: {
            success: correctionState === CORRECT,
        },
        context: getContext(),
    };
};

export const onTerminateHomework = (state) => {
    const homeworkId = state.homework.id;
    const isPreview = state.application.isPreview;
    let statementId;
    const bookTitle = state.homework.bookTitle;
    const subject = state.homework.subjects && state.homework.subjects.length > 0 && state.homework.subjects[0].name;
    const exercisesCount = state.homework.exercises.length;

    const courseTitle = state.homework.courseTitle;
    const isbn = state?.homework?.isbn;

    if (!homeworkId && isPreview) {
        statementId = 'preview';
    } else {
        statementId = homeworkId;
    }

    let profile = {
        bookId: state.status.simulationOfBookId ? state.status.simulationOfBookId : state.application.bookId,
        isPreview: state.application.isPreview,
        saveStatus: state.application.saveStatus,
        name: state.application.name,
        shuffle: state.application.shuffle,
        pauseTest: state.application.pauseTest,
        repeatTest: state.application.repeatTest,
        showSuggestions: state.application.showSuggestions,
        singleAnswer: state.application.singleAnswer,
        time: state.application.time,
    };

    const exercises = state.homework.exercises.map((item) => {
        const correctionStatus = state.status.correctionsStatus.find((correctionStatus) => correctionStatus.exerciseId === item.id);
        const status = correctionStatus ? correctionStatus.state : MISSING;

        return {
            id: item.id,
            difficulty: item.difficulty,
            type: exerciseTypes.getExerciseTypeFromName(item.typeLabel).split(' ').join('-').toLowerCase(),
            completion: status !== MISSING,
            success: status === CORRECT,
        };
    });

    return {
        actor: getActor(),
        verb: {
            id: `${VERB_URL}/verbs/completed`,
        },
        object: {
            objectType: 'Activity',
            id: `${X_API_ZANICHELLI}/activities/homework/${statementId}`,
            definition: {
                extensions: getOnTerminateExtensions(bookTitle, subject, exercisesCount, exercises, courseTitle, profile, isbn),
            },
        },
        context: getContext(),
    };
};

export const getOnGetHomeworkExtensions = (homeworkId, profile, exercisesCount, exercisesIds, subject, courseTitle, bookTitle, isbn) => {
    let extensions = {};
    if (isbn) {
        extensions[`${X_API_ZANICHELLI}/keys/book/isbn`] = isbn;
    }
    if (homeworkId) {
        extensions[`${X_API_ZANICHELLI}/keys/start/homework/id`] = homeworkId;
    }
    if (subject) {
        extensions[`${X_API_ZANICHELLI}/keys/start/homework/matter`] = subject;
    }
    if (courseTitle) {
        extensions[`${X_API_ZANICHELLI}/keys/start/homework/courseTitle`] = courseTitle;
    }
    if (bookTitle) {
        extensions[`${X_API_ZANICHELLI}/keys/start/homework/bookTitle`] = bookTitle;
    }

    extensions[`${X_API_ZANICHELLI}/keys/start/homework/profile`] = profile;
    extensions[`${X_API_ZANICHELLI}/keys/start/homework/exercisesCount`] = exercisesCount;
    extensions[`${X_API_ZANICHELLI}/keys/start/homework/exercises`] = exercisesIds;

    return extensions;
};

export const onGetHomework = (state) => {
    const homeworkId = state.homework.id;
    const exercisesCount = state.homework.exercises.length;
    const exercisesIds = state.homework.exercises.map((exercise) => exercise.id);
    const subject = state.homework.subjects && state.homework.subjects.length > 0 && state.homework.subjects[0].name;

    const courseTitle = state.homework.courseTitle;
    const bookTitle = state.homework.bookTitle;

    let statementId;

    let profile = {
        bookId: state.status.simulationOfBookId ? state.status.simulationOfBookId : state.application.bookId,
        isPreview: state.application.isPreview,
        saveStatus: state.application.saveStatus,
        name: state.application.name,
        shuffle: state.application.shuffle,
        pauseTest: state.application.pauseTest,
        repeatTest: state.application.repeatTest,
        showSuggestions: state.application.showSuggestions,
        singleAnswer: state.application.singleAnswer,
        time: state.application.time,
    };

    if (state.application.originalId) {
        profile.originalId = state.application.originalId;
    }
    const isbn = state?.homework?.isbn;
    if (!homeworkId && profile.isPreview) {
        statementId = 'preview';
    } else {
        statementId = homeworkId;
    }

    const openedFrom = state.application.openedFrom;

    return {
        actor: getActor(),
        verb: {
            id: `${VERB_URL}/verbs/started`,
        },
        object: {
            objectType: 'Activity',
            id: `${X_API_ZANICHELLI}/activities/homework/${statementId}`,
            definition: {
                extensions: getOnGetHomeworkExtensions(
                    homeworkId,
                    profile,
                    exercisesCount,
                    exercisesIds,
                    subject,
                    courseTitle,
                    bookTitle,
                    isbn,
                ),
            },
        },
        context: getStartedContext(openedFrom),
    };
};

export const onShowHomeworkCorrection = (state) => {
    const correctionId = state?.application?.statusId;

    const homeworkId = parseHomeworkId(state.homework.id, state.homework.idCat, state.application.source);

    return {
        actor: getActor(),
        verb: {
            id: `${VERB_URL}/verbs/opened`,
        },
        object: {
            objectType: 'Activity',
            id: `${X_API_ZANICHELLI}/activities/correction/${correctionId}`,
            definition: {
                extensions: {
                    [`${X_API_ZANICHELLI}/opened/correction_of/homework/id`]: `${homeworkId}`,
                },
            },
        },
        context: getContext(),
    };
};
